import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, FlaskConical, RefreshCw, MoreHorizontal, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api';
import EmptyState from '../EmptyState';
import { useAuth } from '../../contexts/AuthContext';

/**
 * The Business Development "Meetings" tab (#2).
 *
 * BD-05: the person's own calendar meetings (2 days back .. 14 days ahead) from
 * /api/bd/calendar/meetings, each with its classification, prep and capture
 * status chips, and a manual override ("Treat as BD" / "Not a BD meeting", plus
 * "Delivery, not BD" and, for meetings Outlook marks Personal / Confidential,
 * "Personal / Confidential Meeting, not BD"). Private meetings are never stored,
 * so they never appear here.
 *
 * BD-04: the admin "Seed test meeting" button and the synthetic test meetings
 * stay, below, so the meeting detail page stays reachable with test data.
 */

interface CalMeeting {
  id: number;
  title: string;
  start_at: string;
  end_at: string;
  is_all_day: boolean;
  status: string;
  classification: 'external' | 'internal' | 'skipped' | null;
  relationship_class: 'known_bd' | 'delivery' | 'unknown_external' | null;
  skip_reason: string | null;
  override: 'treat_as_bd' | 'ignore' | 'delivery' | null;
  is_recurring: boolean;
  sensitivity: 'normal' | 'personal' | 'confidential';
  attendees: { email: string; name: string | null; internal: boolean; match: string | null }[];
  prep_status: string | null;
  capture_status: string | null;
  brief_status: string | null;
}

interface SyntheticMeeting { id: number; title: string; created_at: string; source?: string }

type Filter = 'asked' | 'all' | 'skipped';

const SKIP_REASON: Record<string, string> = {
  declined: 'Declined',
  cancelled: 'Cancelled',
  all_day: 'All-day',
  free_busy: 'Marked Free',
  skip_keyword: 'Skip-list word',
  series_rule: 'Series skipped',
  title_rule: 'Title skipped',
  sensitivity_rule: 'Personal/Confidential skipped',
  delivery_series: 'Delivery series',
};

const REL: Record<string, { label: string; cls: string }> = {
  known_bd: { label: 'Known BD', cls: 'bg-green-50 text-green-700 border-green-200' },
  delivery: { label: 'Delivery', cls: 'bg-purple-50 text-purple-700 border-purple-200' },
  unknown_external: { label: 'Prospect', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
};

/** Mirrors backend classify.shouldEmit: is the assistant going to ask about it? */
function isAsked(m: CalMeeting): boolean {
  if (m.status !== 'active') return false;
  if (m.override === 'ignore' || m.override === 'delivery') return false;
  if (m.override === 'treat_as_bd') return true;
  return m.classification === 'external' && m.skip_reason !== 'delivery_series';
}

// `zone` is the person's resolved time zone from the server (Outlook, else their Slack
// quiet-hours zone, else a labelled default), the same one the Slack brief uses. Without
// one the browser's zone is used. The zone is always shown ("2:00 PM–3:00 PM MDT"): a bare
// clock time is ambiguous for a multi-zone team.
function when(m: CalMeeting, zone: string | null = null): string {
  const s = new Date(m.start_at);
  const tz = zone || undefined;
  if (m.is_all_day) {
    return `${s.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })} · all day`;
  }
  const day = s.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', timeZone: tz });
  const t = (d: Date) => d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', timeZone: tz });
  const zoneName = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
    .formatToParts(s)
    .find((p) => p.type === 'timeZoneName')?.value || '';
  return `${day} · ${t(s)}–${t(new Date(m.end_at))} ${zoneName}`.trim();
}

function who(m: CalMeeting): string {
  const ext = m.attendees.filter((a) => !a.internal);
  if (!ext.length) return 'Internal only';
  const first = ext[0].name || ext[0].email;
  return ext.length > 1 ? `${first} +${ext.length - 1}` : first;
}

function prepChip(m: CalMeeting): { label: string; cls: string } {
  if (m.brief_status === 'sent' || m.prep_status === 'done') return { label: 'Prep: sent', cls: 'bg-green-50 text-green-700 border-green-200' };
  if (!isAsked(m) || m.prep_status === 'skipped') return { label: 'Prep: —', cls: 'bg-gray-50 text-gray-400 border-gray-200' };
  if (m.brief_status === 'pending') return { label: 'Prep: scheduled', cls: 'bg-blue-50 text-blue-700 border-blue-200' };
  return { label: 'Prep: waiting', cls: 'bg-blue-50 text-blue-700 border-blue-200' };
}

function captureChip(m: CalMeeting): { label: string; cls: string } {
  if (m.capture_status === 'done') return { label: 'Capture: done', cls: 'bg-green-50 text-green-700 border-green-200' };
  if (!isAsked(m) || m.capture_status === 'skipped') return { label: 'Capture: —', cls: 'bg-gray-50 text-gray-400 border-gray-200' };
  return { label: new Date(m.end_at) > new Date() ? 'Capture: after call' : 'Capture: waiting', cls: 'bg-blue-50 text-blue-700 border-blue-200' };
}

const Chip: React.FC<{ label: string; cls: string; testid?: string }> = ({ label, cls, testid }) => (
  <span className={`inline-block px-2 py-0.5 rounded-full border text-[11px] font-medium whitespace-nowrap ${cls}`} data-testid={testid}>{label}</span>
);

const RowMenu: React.FC<{ m: CalMeeting; onAnswer: (m: CalMeeting, a: string) => void; onUndo: (m: CalMeeting) => void }> = ({ m, onAnswer, onUndo }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  const item = (label: string, answer: string, hint?: string) => (
    <button
      type="button"
      onClick={() => { setOpen(false); onAnswer(m, answer); }}
      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
      data-testid={`bd-meeting-${m.id}-${answer}`}
    >
      <div className="text-gray-800">{label}</div>
      {hint && <div className="text-xs text-gray-400">{hint}</div>}
    </button>
  );
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
        aria-label="Meeting options"
        data-testid={`bd-meeting-menu-${m.id}`}
      >
        <MoreHorizontal className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-64 bg-white border rounded-lg shadow-lg z-20 py-1">
          {item('Treat as BD', 'bd')}
          {item('Not a BD meeting', 'not_bd', m.is_recurring ? 'Stops asking for the whole series' : undefined)}
          {m.classification === 'external' && item('Delivery, not BD', 'delivery', m.is_recurring ? 'Learned for the whole series' : undefined)}
          {m.sensitivity === 'personal' && item('Personal Meeting, not BD', 'personal_not_bd')}
          {m.sensitivity === 'confidential' && item('Confidential Meeting, not BD', 'confidential_not_bd')}
          {m.override && (
            <button
              type="button"
              onClick={() => { setOpen(false); onUndo(m); }}
              className="w-full text-left px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 border-t"
              data-testid={`bd-meeting-${m.id}-undo`}
            >
              Undo my choice for this meeting
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const MeetingsTab: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = ['ADMIN', 'MASTER', 'SUPER_ADMIN'].includes(user?.role || '');
  const [meetings, setMeetings] = useState<CalMeeting[]>([]);
  const [sync, setSync] = useState<{ last_synced_at: string | null; last_error: string | null; live_updates: boolean } | null>(null);
  const [tzInfo, setTzInfo] = useState<{ zone: string; source: string } | null>(null);
  const [synthetic, setSynthetic] = useState<SyntheticMeeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [filter, setFilter] = useState<Filter>('asked');
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cal, syn] = await Promise.all([
        api.get('/api/bd/calendar/meetings'),
        isAdmin ? api.get('/api/bd-meetings').catch(() => ({ data: { meetings: [] } })) : Promise.resolve({ data: { meetings: [] } }),
      ]);
      setMeetings(cal.data?.meetings || []);
      setSync(cal.data?.sync || null);
      setTzInfo(cal.data?.timezone || null);
      setSynthetic(((syn.data?.meetings || []) as SyntheticMeeting[]).filter((m) => !m.source || m.source === 'synthetic'));
    } catch (err) {
      toast.error('Failed to load meetings');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  const syncNow = async () => {
    setSyncing(true);
    setNotice(null);
    try {
      const { data } = await api.post('/api/bd/calendar/sync');
      toast.success(`Calendar synced (${data.synced || 0} change${data.synced === 1 ? '' : 's'})`);
      await load();
    } catch (err: any) {
      const code = err?.response?.data?.code;
      if (code === 'assistant_off') setNotice('Your BD Assistant is switched off, so your calendar is not being watched.');
      else if (code === 'outlook_not_connected') setNotice('Connect your Outlook in BD Assistant settings to see your meetings here.');
      else toast.error(err?.response?.data?.error || 'Could not sync your calendar');
    } finally {
      setSyncing(false);
    }
  };

  const answer = async (m: CalMeeting, a: string) => {
    try {
      const { data } = await api.post(`/api/bd/calendar/meetings/${m.id}/answer`, { answer: a });
      if (data.series_rule) toast.success('Saved for the whole series — undo it in BD Assistant settings');
      else toast.success('Saved');
      if (data.proposal_id) toast('A skip rule was suggested in Approvals', { icon: '💡' });
      await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not save');
    }
  };

  const undo = async (m: CalMeeting) => {
    try {
      await api.delete(`/api/bd/calendar/meetings/${m.id}/override`);
      toast.success('Undone');
      await load();
    } catch {
      toast.error('Could not undo');
    }
  };

  const seedTestMeeting = async () => {
    setSeeding(true);
    try {
      await api.post('/api/bd-meetings/test-seed');
      toast.success('Created a synthetic test meeting');
      await load();
    } catch (err) {
      toast.error('Failed to seed a test meeting');
    } finally {
      setSeeding(false);
    }
  };

  if (loading) return <div className="text-sm text-gray-400 py-8 text-center">Loading…</div>;

  const shown = meetings.filter((m) => (filter === 'asked' ? isAsked(m) : filter === 'skipped' ? !isAsked(m) : true));
  const counts = { asked: meetings.filter(isAsked).length, all: meetings.length, skipped: meetings.filter((m) => !isAsked(m)).length };

  return (
    <div data-testid="bd-meetings-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="inline-flex rounded-lg border overflow-hidden text-sm" role="tablist">
          {([['asked', 'Will ask about'], ['all', 'All'], ['skipped', 'Not asked']] as [Filter, string][]).map(([f, label]) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 ${filter === f ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}
              data-testid={`bd-meetings-filter-${f}`}
            >
              {label} <span className="text-xs text-gray-400">{counts[f]}</span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-400" data-testid="bd-meetings-sync-status">
            {sync?.last_error
              ? 'Last sync failed'
              : sync?.last_synced_at
                ? `Synced ${new Date(sync.last_synced_at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}${sync.live_updates ? ' · live' : ''}`
                : 'Not synced yet'}
          </span>
          {tzInfo?.source === 'default' && (
            <span className="text-xs text-amber-600" data-testid="bd-meetings-timezone-note">
              Times are shown in Eastern time because your time zone is not set (Settings → Notifications → Quiet hours).
            </span>
          )}
          <button
            type="button"
            onClick={syncNow}
            disabled={syncing}
            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded border text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            data-testid="bd-meetings-sync"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} /> Sync now
          </button>
        </div>
      </div>

      {notice && <div className="mb-3 text-sm rounded-lg border border-amber-200 bg-amber-50 text-amber-800 px-3 py-2" data-testid="bd-meetings-notice">{notice}</div>}

      {meetings.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No meetings yet"
          description="Your meetings from the last 2 days and the next 14 appear here once your BD Assistant is on and your Outlook is connected. Private meetings are never shown or stored."
        />
      ) : shown.length === 0 ? (
        <div className="text-sm text-gray-400 py-8 text-center border border-dashed rounded-lg">Nothing in this view.</div>
      ) : (
        <div className="border rounded-lg divide-y">
          {shown.map((m) => {
            const rel = m.classification === 'external' && m.relationship_class ? REL[m.relationship_class] : null;
            const prep = prepChip(m);
            const cap = captureChip(m);
            return (
              <div key={m.id} className="px-4 py-3 flex items-start gap-3 hover:bg-gray-50" data-testid={`bd-meeting-row-${m.id}`}>
                <button type="button" onClick={() => navigate(`/app/bd-meetings/${m.id}`)} className="flex-1 min-w-0 text-left">
                  <div className="text-xs text-gray-400">{when(m, tzInfo?.zone || null)}{m.is_recurring ? ' · recurring' : ''}</div>
                  <div className="text-sm font-medium text-gray-900 truncate">{m.title}</div>
                  <div className="text-xs text-gray-500 truncate">{who(m)}</div>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {m.classification === 'external' && <Chip label="External" cls="bg-blue-50 text-blue-700 border-blue-200" testid={`bd-meeting-class-${m.id}`} />}
                    {m.classification === 'internal' && <Chip label="Internal" cls="bg-gray-50 text-gray-600 border-gray-200" testid={`bd-meeting-class-${m.id}`} />}
                    {m.classification === 'skipped' && <Chip label={`Skipped · ${SKIP_REASON[m.skip_reason || ''] || 'skipped'}`} cls="bg-gray-50 text-gray-500 border-gray-200" testid={`bd-meeting-class-${m.id}`} />}
                    {rel && <Chip label={rel.label} cls={rel.cls} />}
                    {m.skip_reason === 'delivery_series' && <Chip label="Not asked · delivery series" cls="bg-gray-50 text-gray-500 border-gray-200" />}
                    {m.sensitivity !== 'normal' && <Chip label={m.sensitivity === 'personal' ? 'Personal' : 'Confidential'} cls="bg-gray-50 text-gray-600 border-gray-200" />}
                    {m.override === 'treat_as_bd' && <Chip label="You: treat as BD" cls="bg-green-50 text-green-700 border-green-200" />}
                    {m.override === 'ignore' && <Chip label="You: not BD" cls="bg-gray-100 text-gray-600 border-gray-200" />}
                    {m.override === 'delivery' && <Chip label="You: delivery" cls="bg-purple-50 text-purple-700 border-purple-200" />}
                    <Chip label={prep.label} cls={prep.cls} testid={`bd-meeting-prep-${m.id}`} />
                    <Chip label={cap.label} cls={cap.cls} testid={`bd-meeting-capture-${m.id}`} />
                  </div>
                </button>
                <RowMenu m={m} onAnswer={answer} onUndo={undo} />
              </div>
            );
          })}
        </div>
      )}

      {isAdmin && (
        <div className="mt-8" data-testid="bd-test-meetings">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">Test meetings</div>
            <button
              onClick={seedTestMeeting}
              disabled={seeding}
              className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded border text-amber-700 border-amber-200 bg-amber-50 hover:bg-amber-100 disabled:opacity-50"
            >
              {seeding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FlaskConical className="w-3.5 h-3.5" />} {seeding ? 'Seeding…' : 'Seed test meeting'}
            </button>
          </div>
          {synthetic.length > 0 && (
            <div className="border rounded-lg divide-y overflow-hidden">
              {synthetic.map((m) => (
                <button
                  key={m.id}
                  onClick={() => navigate(`/app/bd-meetings/${m.id}`)}
                  className="w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center justify-between"
                >
                  <span className="text-sm text-gray-700">{m.title}</span>
                  <span className="text-xs text-gray-400">{new Date(m.created_at).toLocaleDateString()}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MeetingsTab;
